import { IProviderProps } from "../interfaces/provider";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { useRef } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { AccountInfo, InteractionStatus } from "@azure/msal-browser";
import { Loading } from "../components/Loading";

interface IAuthContext {
    isAuthed: boolean
    getAccessToken: () => Promise<string | undefined>
    getAccount: () => AccountInfo | undefined
}

const authContext = createContext<IAuthContext>({
    isAuthed: false,
    getAccessToken: async () => (""),
    getAccount: () => undefined
})


const AuthProvider = (props: IProviderProps) => {
    const { instance, accounts, inProgress } = useMsal()
    const isAuthenticated = useIsAuthenticated();
    const [account, setAccount] = useState<AccountInfo | undefined>(undefined)
    const [apiToken, setApiToken] = useState<string>()
    const loginStartedRef = useRef(false)
    const tokenRedirectStartedRef = useRef(false)

    // useEffect(() => {
    //     if (inProgress === InteractionStatus.None && !isAuthenticated) {
    //         instance.loginRedirect(loginRequest).catch(e => {
    //             console.error("Error: ", e)
    //         }).then((e) => {
    //             console.log("Accounts", accounts)
    //             setAccount(accounts[0])
    //             navigate('/')
    //         })
    //     }
    //
    // }, [])

    useEffect(() => {
        const active = instance.getActiveAccount();
        if (!active && accounts.length > 0) {
            instance.setActiveAccount(accounts[0])
            setAccount(accounts[0])
        }
    }, [accounts, instance])

    useEffect(() => {
        if (inProgress !== InteractionStatus.None) {
            return
        }
        if (!isAuthenticated && accounts.length < 1) {
            if (!loginStartedRef.current) {
                loginStartedRef.current = true
                instance.loginRedirect(loginRequest).catch(e => {
                    console.error("Error: ", e)
                })
            }
        }
    }, [accounts.length, inProgress, instance, isAuthenticated])


    const getAccount = useCallback(() => {
        const active = instance.getActiveAccount()
        if (active) {
            return active
        }
        if (accounts.length > 0) {
            instance.setActiveAccount(accounts[0])
            setAccount(accounts[0])
            return accounts[0]
        }
        return account
    }, [account, accounts, instance])


    const getAccessToken = useCallback(async (): Promise<string | undefined> => {
        if (inProgress !== InteractionStatus.None) {
            return
        }
        const activeAccount = instance.getActiveAccount() ?? account ?? accounts[0]
        if (!activeAccount) {
            return
        }
        if (!instance.getActiveAccount()) {
            instance.setActiveAccount(activeAccount)
        }
        const request = {
            ...loginRequest,
            account: activeAccount
        };
        // Silently acquires an access token which is then attached to a request for Microsoft Graph data
        try {
            const auth = await instance.acquireTokenSilent(request)
            if (auth) {
                const token = auth.accessToken
                if (!token) {
                    return
                }

                setApiToken(token);
                return token
            }
        } catch (e) {
            console.error(e)
            if (!tokenRedirectStartedRef.current && inProgress === InteractionStatus.None) {
                tokenRedirectStartedRef.current = true
                await instance.acquireTokenRedirect(request)
            }
        }
    }, [account, accounts, inProgress, instance])

    useEffect(() => {
        if (inProgress !== InteractionStatus.None) {
            return
        }
        if (!apiToken && (isAuthenticated || accounts.length > 0)) {
            getAccessToken()
        }
    }, [accounts.length, apiToken, getAccessToken, inProgress, isAuthenticated])

    const value = useMemo(() => ({
        isAuthed: isAuthenticated,
        getAccessToken,
        getAccount,
    }), [getAccessToken, getAccount, isAuthenticated])

    // inProgress is an enum value from MSAL, not a string
    if (inProgress === InteractionStatus.Startup) {
        return <Loading />
    }

    if (!apiToken) {
        return <Loading />
    }

    return (
        <authContext.Provider value={value}>
            {props.children}
        </authContext.Provider>
    )


}

export {
    AuthProvider, authContext
}
